import axios from 'axios';

import i18n, { loadTranslationDomain } from '@shop/utils/Translation';

export interface ApiErrorPayload {
  message: string;
  translate: boolean;
  domain?: string;
  parameters?: Record<string, unknown>;
}

interface ApiErrorResponse {
  label?: string;
  label_translation_domain?: string;
  label_parameters?: Record<string, unknown>;
  message?: string;
  exception?: ApiErrorResponse;
  errors?: {
    children?: Record<string, { errors?: string[] }>;
    errors?: Record<string, string[]>;
  };
}

function findFormErrorMessage(response: ApiErrorResponse): string | null {
  if (response.errors?.children) {
    const childKey = Object.keys(response.errors.children).find(key => {
      const fieldError = response.errors?.children?.[key];
      return (fieldError?.errors?.length ?? 0) > 0;
    });
    if (childKey) {
      const fieldError = response.errors.children[childKey];
      return `${childKey}: ${fieldError.errors?.[0] ?? ''}`;
    }
  }

  if (response.errors?.errors) {
    const errorKey = Object.keys(response.errors.errors).find(
      key => (response.errors?.errors?.[key].length ?? 0) > 0
    );
    if (errorKey) {
      return response.errors.errors[errorKey][0];
    }
  }

  return null;
}

export function extractApiError(err: unknown): ApiErrorPayload {
  if (!axios.isAxiosError(err) || !err.response?.data) {
    return { message: 'error_fallback', translate: true, domain: 'messages' };
  }

  let response = err.response.data as ApiErrorResponse;
  if (response.exception) {
    response = response.exception;
  }

  if (response.label && response.label !== 'exception.http_error') {
    return {
      message: response.label,
      translate: true,
      domain: response.label_translation_domain ?? 'messages',
      parameters: response.label_parameters,
    };
  }

  if (response.label && response.message && response.message !== 'An error occurred.') {
    return { message: response.message, translate: false };
  }

  const formError = findFormErrorMessage(response);
  if (formError) {
    return { message: formError, translate: false };
  }

  return { message: 'error_fallback', translate: true, domain: 'messages' };
}

function interpolateSymfonyParams(text: string, parameters?: Record<string, unknown>): string {
  if (!parameters) {
    return text;
  }

  return Object.entries(parameters).reduce(
    (result, [key, value]) => result.replaceAll(`{{${key}}}`, String(value)),
    text
  );
}

export async function resolveApiErrorMessage(payload: ApiErrorPayload): Promise<string> {
  if (!payload.translate) {
    return payload.message;
  }

  const domain = payload.domain ?? 'messages';
  await loadTranslationDomain(domain);

  const translated = i18n.global.t(payload.message, payload.parameters ?? {});
  if (translated !== payload.message) {
    return interpolateSymfonyParams(translated, payload.parameters);
  }

  if (domain !== 'messages') {
    await loadTranslationDomain('messages');
    const fallback = i18n.global.t('error_fallback');
    if (fallback !== 'error_fallback') {
      return fallback;
    }
  }

  return translated;
}

export async function showApiError(
  err: unknown,
  flashError: (message: string) => void
): Promise<void> {
  const message = await resolveApiErrorMessage(extractApiError(err));
  flashError(message);
}

export async function showTranslatedFlashError(
  key: string,
  flashError: (message: string) => void,
  domain = 'messages'
): Promise<void> {
  const message = await resolveApiErrorMessage({ message: key, translate: true, domain });
  flashError(message);
}
